// Google Ads enhanced conversions (GH-1130). The signed-in user's email is
// normalised and SHA-256 hashed in the browser and handed to gtag as
// user_data, so Ads can match conversions to signed-in Google users. Only
// the hex hash ever reaches gtag (and the dataLayer GTM shares); the raw
// email never does.
//
// Marketing consent first. The choice is read ONLY through
// src/lib/cookieConsent.js (GH-1134, the landing's contract): currentConsent()
// (window.dathentConsent, else the stored 'dathent-cookie-consent' v1 record)
// and its 'dathent:consent' event for a new choice from the app's cookie
// banner. No recorded choice, or "Necessary only", means no user_data. The
// marketing flag is the same one index.html's bootstrap maps to Consent
// Mode's ad_user_data, so user_data is set only when ad_user_data is granted.
//
// Ordering: gtag applies user_data to the hits pushed AFTER the 'set'.
// index.html's bootstrap listener (the consent update) runs first;
// initAdsUserData() registers this module's listener at boot, before gtag.js
// registers the listener that flushes the conversions it held until the banner
// was answered (that one is added lazily, on the first held event). So on an
// Accept the order is: consent update, user_data, held conversions. And
// AuthContext awaits the hash before the signup conversion.
//
// Withdrawn consent: Consent Mode then sets ad_user_data to denied, and
// Google tags do not send user-provided data in that state.

import { CONSENT_EVENT, currentConsent } from './cookieConsent.js';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOTLESS_DOMAINS = new Set(['gmail.com', 'googlemail.com']);

function browserWindow() {
  return typeof window !== 'undefined' ? window : undefined;
}

/** Google's normalisation for hashed emails: trim, lowercase, no dots in a gmail/googlemail local part. Null when not an email. */
export function normalizeEmailForAds(email) {
  if (typeof email !== 'string') return null;
  const value = email.trim().toLowerCase();
  if (!EMAIL_SHAPE.test(value)) return null;
  const at = value.indexOf('@');
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  return DOTLESS_DOMAINS.has(domain) ? `${local.replace(/\./g, '')}@${domain}` : value;
}

/** Lowercase hex SHA-256 of the UTF-8 text (Web Crypto). Rejects without Web Crypto. */
export async function sha256Hex(text, subtle = globalThis.crypto?.subtle) {
  if (!subtle || typeof subtle.digest !== 'function') throw new Error('Web Crypto unavailable');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(String(text)));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** True only for a recorded choice that includes marketing (cookieConsent.currentConsent, see the header). */
export function hasMarketingConsent(win = browserWindow()) {
  try {
    if (!win) return false;
    return currentConsent(win)?.marketing === true;
  } catch {
    return false;
  }
}

export function createAdsUserData(options = {}) {
  const getWin = () => options.win ?? browserWindow();
  const subtle = 'subtle' in options ? options.subtle : undefined;
  let hash = null;      // current identity's hashed email
  let applied = null;   // last hash handed to gtag
  let generation = 0;   // newest setEmail call wins
  let listening = false;

  function apply(detail) {
    try {
      if (!hash || hash === applied) return false;
      const win = getWin();
      const granted = detail && typeof detail === 'object' && typeof detail.marketing === 'boolean'
        ? detail.marketing
        : hasMarketingConsent(win);
      if (!granted) return false;
      const gtag = win?.gtag;
      if (typeof gtag !== 'function') return false;
      gtag('set', 'user_data', { sha256_email_address: hash });
      applied = hash;
      return true;
    } catch {
      return false;
    }
  }

  /** Remember the signed-in user's email (null on sign-out) and apply it if consent allows. Never rejects. */
  async function setEmail(email) {
    const mine = ++generation;
    try {
      const normalized = normalizeEmailForAds(email);
      if (!normalized) {
        hash = null;
        return false;
      }
      const next = await sha256Hex(normalized, subtle);
      if (mine !== generation) return false;
      hash = next;
      return apply();
    } catch {
      if (mine === generation) hash = null;
      return false;
    }
  }

  /** Apply on every new consent choice. Idempotent; false when there is no document. */
  function listen() {
    try {
      if (listening) return true;
      const doc = getWin()?.document;
      if (typeof doc?.addEventListener !== 'function') return false;
      doc.addEventListener(CONSENT_EVENT, (event) => { apply(event?.detail); });
      listening = true;
      return true;
    } catch {
      return false;
    }
  }

  return { setEmail, apply, listen };
}

const shared = createAdsUserData();

/** AuthContext: the current user's email, or null on sign-out. */
export function setAdsUserEmail(email) {
  return shared.setEmail(email);
}

/** main.jsx, once at boot: start following consent choices. */
export function initAdsUserData() {
  return shared.listen();
}
